/**
 * "Blood tests" comparison cards — the recommended Ahead membership card and
 * the one-off "Single blood test" baseline card, shown side by side.
 *
 * Built from the Ahead design with a fluid flex-column layout; type, colours,
 * gradients and radii follow the design system.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductUspsModal } from "./card/ProductUSPs";
import { MembershipDetailsModal } from "./card/MembershipDetailsModal";
import { resolveProductKey } from "@/locales";
import { Product } from "./types";
import { BookingLanguage } from "@/types/booking";
import { languageMap, BOOKING_NS } from "@/i18n";
import { BENEFIT_ROWS } from "./MembershipBenefits";
import { MembershipTier } from "@/hooks/createProductsQueryFn";

/** Subtle lavender wash on the membership card surface (oklab gradient). */
const MEMBERSHIP_GRADIENT =
  "linear-gradient(in oklab 127deg, #FAFBFF 0%, #FFFFFF 22%, #F5F8FF 100%)";

/* ---------------------------------------------------------------- glyphs --- */

/** Olive check in a soft-green disc — the "included" marker. */
const CheckDisc = () => (
  <span className="flex size-[22px] flex-shrink-0 items-center justify-center rounded-full bg-[#DBF0DC96]">
    <svg
      width="12"
      height="8"
      viewBox="0 0 13 8"
      fill="none"
      aria-hidden
      className="overflow-visible"
    >
      <path
        d="M0 4 4.444 8 13 0"
        fill="none"
        stroke="#408C49"
        strokeWidth="1.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  </span>
);

/** Member-discount "rosette" glyph (priced rows). */
const DiscountGlyph = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 19 19"
    fill="none"
    aria-hidden
    className="flex-shrink-0"
  >
    <path
      fillRule="evenodd"
      fill="#408A49"
      d="M9.5 19c-.361 0-.706-.068-1.033-.203a2.66 2.66 0 0 1-.871-.585c-.502-.497-.934-.821-1.293-.974-.36-.154-.899-.23-1.618-.23-.748 0-1.384-.262-1.907-.785a2.6 2.6 0 0 1-.786-1.908c0-.712-.077-1.25-.23-1.614-.154-.363-.478-.796-.974-1.297a2.66 2.66 0 0 1-.587-.871A2.57 2.57 0 0 1 0 9.502c0-.357.067-.699.201-1.027.134-.328.33-.621.587-.879.496-.498.82-.927.974-1.288.153-.361.23-.902.23-1.623 0-.748.262-1.384.786-1.908a2.6 2.6 0 0 1 1.907-.785c.712 0 1.25-.077 1.614-.23.363-.154.796-.478 1.297-.974.254-.255.544-.45.872-.585A2.57 2.57 0 0 1 9.496 0c.358 0 .701.067 1.029.201.328.134.621.33.879.587.498.496.927.82 1.288.974.361.153.902.23 1.623.23.748 0 1.384.262 1.908.785.523.524.785 1.16.785 1.908 0 .712.077 1.25.23 1.614.154.363.478.796.974 1.297.257.258.453.55.587.877.134.327.201.669.201 1.025 0 .357-.067.699-.201 1.027a2.66 2.66 0 0 1-.587.879c-.497.502-.821.934-.974 1.293-.154.36-.23.899-.23 1.618 0 .748-.262 1.384-.785 1.908a2.6 2.6 0 0 1-1.908.785c-.712 0-1.25.077-1.614.23-.363.154-.796.478-1.297.974a2.66 2.66 0 0 1-.871.585A2.57 2.57 0 0 1 9.5 19Zm2.435-5.836c.345 0 .636-.119.873-.356.237-.237.355-.528.355-.873 0-.346-.118-.639-.355-.881-.237-.241-.528-.362-.873-.362-.346 0-.639.121-.881.362-.241.242-.362.535-.362.881 0 .345.121.636.362.873.242.237.535.356.881.356ZM7.017 13.027l6.01-6-1.054-1.054-6 6.01 1.044 1.044Zm.929-5.081c.241-.242.362-.535.362-.881 0-.345-.121-.636-.362-.873-.237-.237-.528-.355-.881-.355-.345 0-.636.118-.873.355-.237.237-.356.528-.356.873 0 .346.119.639.356.881.237.237.528.358.873.358.353 0 .644-.121.881-.358Z"
    />
  </svg>
);

/** Blood-tube glyph in a soft-green disc (for "Upload existing lab data"). */
const TubeDisc = () => (
  <span className="flex size-[22px] flex-shrink-0 items-center justify-center rounded-full bg-[#DBF0DC96]">
    <svg width="9" height="13" viewBox="0 0 13 19" fill="none" aria-hidden>
      <path
        fillRule="evenodd"
        fill="#408B49"
        d="M6.5 19c-1.294 0-2.396-.451-3.307-1.352-.911-.901-1.366-1.995-1.366-3.283V5.327c-.505 0-.936-.177-1.292-.53A1.74 1.74 0 0 1 0 3.51V1.827C0 1.322.178.891.535.535.891.178 1.322 0 1.827 0h9.336c.505 0 .938.178 1.297.535.36.356.54.787.54 1.292V3.51c0 .505-.18.934-.54 1.287-.359.353-.792.53-1.297.53v9.183c-.038 1.255-.509 2.317-1.414 3.186C8.845 18.565 7.762 19 6.5 19ZM1.827 3.827h9.336c.09 0 .169-.031.236-.092a.31.31 0 0 0 .101-.225V1.827a.32.32 0 0 0-.101-.231.33.33 0 0 0-.236-.096H1.827a.33.33 0 0 0-.231.096.32.32 0 0 0-.096.231V3.51c0 .089.032.165.096.225a.32.32 0 0 0 .231.092Zm6.914 12.764c.615-.605.922-1.347.922-2.226v-.375H6.567v-1.499h3.096v-2.424H6.567v-1.5h3.096V5.327H3.327v9.038c0 .879.309 1.621.928 2.226.618.606 1.367.909 2.245.909.878 0 1.625-.303 2.241-.909Z"
      />
    </svg>
  </span>
);

/** Muted em-dash — the "not included" marker on the baseline card. */
const DashMark = () => (
  <span className="flex size-[22px] flex-shrink-0 items-center justify-center text-[14px] leading-none text-[#757575]">
    —
  </span>
);

/** Top-right info "i" — soft grey disc. */
const InfoIcon = ({
  label,
  onClick,
}: {
  label: string;
  onClick?: () => void;
}) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="group flex-shrink-0 cursor-pointer normal-case"
  >
    {/* Same info "i" as the full-body check-up (Package) cards — #F4F4F4 disc,
        #919094 glyph — plus a hover darken. */}
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden>
      <circle
        cx="11"
        cy="11"
        r="11"
        className="fill-[#F4F4F4] transition-colors group-hover:fill-[#E5E5E5]"
      />
      <path
        className="fill-[#919094] transition-colors group-hover:fill-[#757575]"
        d="M10.77 6.42c.31 0 .57.1.77.31.2.2.3.46.3.77 0 .31-.1.57-.3.77-.2.21-.46.31-.77.31-.31 0-.57-.1-.77-.31a1.06 1.06 0 0 1-.3-.77c0-.31.1-.57.3-.77.2-.21.46-.31.77-.31ZM9.94 9.94h1.66v5.97H9.94V9.94Z"
      />
    </svg>
  </button>
);

const ArrowRight = ({ color }: { color: string }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    className="flex-shrink-0"
  >
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Chip = ({ children }: { children: React.ReactNode }) => (
  <span className="ml-auto inline-flex flex-shrink-0 items-center rounded-[40px] bg-[#DFE9FA] px-3 py-0.5 text-[11px] font-medium uppercase leading-4 tracking-[0.1px] text-[#001A51]">
    {children}
  </span>
);

/* --------------------------------------------------------- membership card --- */

export const MembershipCard = ({
  onStart,
  infoProduct,
  language,
  membershipTiers,
}: {
  onStart?: () => void;
  infoProduct?: Product | null;
  language: BookingLanguage;
  membershipTiers?: MembershipTier[];
}) => {
  const [showInfo, setShowInfo] = useState(false);
  const { t } = useTranslation(BOOKING_NS, { lng: languageMap[language] });

  const cheapestTier = membershipTiers?.[0];
  const monthlyEquivalent = cheapestTier?.annual_amount_chf
    ? Math.round(cheapestTier.annual_amount_chf / 100 / 12)
    : null;
  const annualPrice = cheapestTier?.annual_amount_chf
    ? Math.round(cheapestTier.annual_amount_chf / 100)
    : null;
  return (
    <div
      className="relative flex h-full rounded-[20px] p-px shadow-[0px_3px_16px_0px_rgba(106,133,161,0.05),0px_8px_24px_0px_rgba(106,133,161,0.05)] transition-shadow duration-300 ease-out hover:shadow-[0px_3px_22px_0px_rgba(106,133,161,0.08),0px_12px_40px_0px_rgba(106,133,161,0.08)]"
      style={{
        backgroundImage:
          "conic-gradient(in oklab from 0deg at 50% 50%, oklab(76.1% -0.001 -0.115) 0%, oklab(83.5% 0.053 -0.084) 20%, oklab(68% 0.053 -0.171) 40%, oklab(66.3% 0.125 -0.116) 60%, oklab(72.4% 0.035 -0.120) 80%, oklab(76.1% -0.001 -0.115) 100%)",
      }}
    >
      <div
        className="flex w-full flex-col rounded-[19px] px-6 pb-6 pt-4"
        style={{ backgroundImage: MEMBERSHIP_GRADIENT }}
      >
        {/* Badges (left) + info (right) */}
        <div className="-mx-2 flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex h-6 items-center rounded-[40px] bg-[#1C7C2F] px-3 text-[11px] font-medium uppercase leading-none text-white">
              {/* nudged 1px down to optically center the all-caps text */}
              <span className="relative top-px">
                {t("bloodTracking.membership.recommended")}
              </span>
            </span>
            <span className="inline-flex h-6 items-center rounded-[40px] bg-[#DFE9FA] px-3 text-[11px] font-medium leading-none tracking-[0.1px] text-[#001A51]">
              {t("bloodTracking.membership.discountBadge")}
            </span>
          </div>
          <InfoIcon
            label={t("bloodTracking.membership.infoAria")}
            onClick={() => setShowInfo(true)}
          />
        </div>

        {/* Title + description */}
        <h3 className="mt-5 text-2xl/9 font-medium text-[#2D2D2D]">
          {t("bloodTracking.membership.title")}
        </h3>
        <p className="mt-1 min-h-11 text-sm/[22px] text-black">
          {t("bloodTracking.membership.shortDesc")}
        </p>

        {/* Price block — min-h kept exact so this card and the baseline align CTAs */}
        <div className="mt-5 min-h-[100px]">
          <p className="text-sm/6 font-medium text-[#0B0B0B]">
            {t("bloodTracking.membership.startingAt")}
          </p>
          <p className="text-[#2D2D2D]">
            <span className="text-[32px] leading-[52px] tracking-[-0.25px]">
              <span className="font-medium">CHF</span>{" "}
              <span className="font-medium">{monthlyEquivalent ?? 25}</span>
            </span>{" "}
            <span className="text-base/6 text-[#757575]">
              {t("bloodTracking.membership.priceSuffix")}
            </span>
          </p>
          <p className="text-sm/6 text-[#757575]">
            {t("bloodTracking.membership.billedAnnually", {
              annual: String(annualPrice ?? 299),
            })}
          </p>
        </div>

        {/* CTA */}
        <button
          type="button"
          onClick={onStart}
          className="group mt-5 flex cursor-pointer items-center justify-center gap-2 rounded-full bg-[#48214A] py-3.5 text-base/6 font-medium normal-case text-white transition-colors hover:bg-[#371938] active:bg-[#290E2A]"
        >
          {t("bloodTracking.membership.cta")}
          <span className="transition-transform duration-200 group-hover:translate-x-0.5">
            <ArrowRight color="#FFFFFF" />
          </span>
        </button>

        {/* Benefits */}
        <ul className="mt-6 flex flex-col">
          {BENEFIT_ROWS.map((row) => (
            <li key={row.key} className="flex min-h-9 items-center gap-3">
              {row.icon === "discount" ? (
                <DiscountGlyph />
              ) : row.icon === "tube" ? (
                <TubeDisc />
              ) : (
                <CheckDisc />
              )}
              <span className="text-sm/5 text-[#2D2D2D]">{t(row.key)}</span>
              {row.chip && (
                <Chip>{t(row.chip, { amount: row.chipAmount })}</Chip>
              )}
            </li>
          ))}
        </ul>
      </div>
      <MembershipDetailsModal
        isOpen={showInfo}
        onClose={() => setShowInfo(false)}
        language={language}
        infoProduct={infoProduct}
        onStart={onStart}
        membershipTiers={membershipTiers}
      />
    </div>
  );
};

/* ----------------------------------------------------------- baseline card --- */

export const BaselineCard = ({
  onSelect,
  infoProduct,
  language,
}: {
  onSelect?: () => void;
  infoProduct?: Product | null;
  language: BookingLanguage;
}) => {
  const [showInfo, setShowInfo] = useState(false);
  const { t } = useTranslation(BOOKING_NS, { lng: languageMap[language] });
  return (
    <div className="flex h-full w-full flex-col rounded-[18px] bg-white px-6 pb-6 pt-4 shadow-[0px_3px_16px_0px_rgba(106,133,161,0.05),0px_8px_24px_0px_rgba(106,133,161,0.05)] transition-shadow duration-300 ease-out hover:shadow-[0px_3px_22px_0px_rgba(106,133,161,0.08),0px_12px_40px_0px_rgba(106,133,161,0.08)]">
      {/* Badge (left) + info (right) */}
      <div className="-mx-2 flex items-start justify-between gap-2">
        <span className="inline-flex h-6 items-center rounded-full border border-[#CACACA] px-5 text-xs font-medium tracking-[0.1px] text-black">
          {t("bloodTracking.baseline.badge")}
        </span>
        <InfoIcon
          label={t("bloodTracking.baseline.infoAria")}
          onClick={() => setShowInfo(true)}
        />
      </div>

      {/* Title + tagline */}
      <h3 className="mt-5 text-2xl/9 font-medium text-[#2D2D2D]">
        {t("bloodTracking.baseline.cardTitle")}
      </h3>
      <p className="mt-1 min-h-11 whitespace-pre-line text-sm/[22px] text-black">
        {t("bloodTracking.baseline.cardTagline")}
      </p>

      {/* Price block — matches the membership min-h so the CTAs line up */}
      <div className="mt-5 min-h-[100px]">
        <p className="text-sm/6 font-medium text-[#757575]">
          {t("bloodTracking.baseline.oneTime")}
        </p>
        <p className="text-[#2D2D2D]">
          <span className="text-[32px] leading-[52px] tracking-[-0.25px]">
            <span className="font-medium">CHF</span>{" "}
            <span className="font-medium">299</span>
          </span>
        </p>
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={onSelect}
        className="group mt-5 flex cursor-pointer items-center justify-center gap-2 rounded-full py-3.5 text-base/6 font-medium normal-case tracking-[0.15px] text-black transition-colors [outline:1px_solid_#B5B5B5] hover:bg-black/[0.04] active:bg-black/[0.08]"
      >
        {t("bloodTracking.baseline.selectCta")}
        <span className="transition-transform duration-200 group-hover:translate-x-0.5">
          <ArrowRight color="#000000" />
        </span>
      </button>

      {/* Benefits — first two included, the rest a muted dash */}
      <ul className="mt-6 flex flex-col">
        {BENEFIT_ROWS.map((row) => (
          <li key={row.key} className="flex min-h-9 items-center gap-3">
            {row.baseline ? <CheckDisc /> : <DashMark />}
            <span
              className={`text-sm/5 ${
                row.baseline ? "text-[#2D2D2D]" : "text-[#757575]"
              }`}
            >
              {t(row.key)}
            </span>
          </li>
        ))}
      </ul>
      {infoProduct && (
        <ProductUspsModal
          product={infoProduct}
          productId={infoProduct.id}
          productName={
            infoProduct.translations?.name ||
            resolveProductKey(language, infoProduct.name)
          }
          language={language}
          isOpen={showInfo}
          onClose={() => setShowInfo(false)}
        />
      )}
    </div>
  );
};
